/**
 * Phase 2G.2 - transaction aging & operational exceptions.
 *
 * Every category below is DETECTED from data that already exists in the
 * schema plus the workflow rules already enforced by the API. Nothing here
 * defines a deadline, SLA or time threshold: the repository contains no
 * business rule that says when a transaction becomes "overdue" or
 * "abandoned", so ages are reported raw (information only) and severity is
 * assigned deterministically from what the data objectively proves:
 *
 *   ERROR     - the stored state contradicts an enforced invariant
 *               (impossible to produce through the API; corruption only)
 *   ATTENTION - a measurable item-count discrepancy exists on the record
 *   INFO      - ordinary operational state worth surfacing (age, queue
 *               position, work in progress) with no rule violated
 */
export const EXCEPTION_CATEGORIES = [
  'TRANSACTION_AGING',
  'WORKFLOW_STUCK',
  'OPEN_PROCESSING_STEP',
  'PROCESSING_DISCREPANCY',
  'PICKUP_NOT_RELEASED',
  'DELIVERY_STEP_DEPTH',
  'ITEM_ACCOUNTABILITY',
  'WORKFLOW_INCONSISTENCY',
] as const;
export type ExceptionCategory = (typeof EXCEPTION_CATEGORIES)[number];

export const EXCEPTION_CATEGORY_LABELS: Record<ExceptionCategory, string> = {
  TRANSACTION_AGING: 'Transaction aging',
  WORKFLOW_STUCK: 'Stuck workflow step',
  OPEN_PROCESSING_STEP: 'Open processing step',
  PROCESSING_DISCREPANCY: 'Processing discrepancy',
  PICKUP_NOT_RELEASED: 'Ready for pick-up',
  DELIVERY_STEP_DEPTH: 'Delivery in progress',
  ITEM_ACCOUNTABILITY: 'Item accountability discrepancy',
  WORKFLOW_INCONSISTENCY: 'Workflow data inconsistency',
};

/** One-line explanation shown next to each category in the UI. */
export const EXCEPTION_CATEGORY_HINTS: Record<ExceptionCategory, string> = {
  TRANSACTION_AGING: 'Time spent in the current status (information only - no deadline is defined)',
  WORKFLOW_STUCK: 'A processing step is open although the transaction has moved outside its status window',
  OPEN_PROCESSING_STEP: 'A processing step was started and has not been finished yet',
  PROCESSING_DISCREPANCY: 'A completed step counted a different number of items than expected',
  PICKUP_NOT_RELEASED: 'Pick-up order is READY but has not been released to the customer',
  DELIVERY_STEP_DEPTH: 'Delivery workflow step the order is currently waiting on',
  ITEM_ACCOUNTABILITY: 'The released/handed-off item snapshot differs from the counted total',
  WORKFLOW_INCONSISTENCY: 'Stored state contradicts an enforced workflow invariant (data corruption)',
};

export const EXCEPTION_SEVERITIES = ['ERROR', 'ATTENTION', 'INFO'] as const;
export type ExceptionSeverity = (typeof EXCEPTION_SEVERITIES)[number];

export const EXCEPTION_SEVERITY_LABELS: Record<ExceptionSeverity, string> = {
  ERROR: 'Error',
  ATTENTION: 'Attention',
  INFO: 'Info',
};

/** Deterministic ordering rank (lowest = shown first). */
export const EXCEPTION_SEVERITY_RANK: Record<ExceptionSeverity, number> = {
  ERROR: 0,
  ATTENTION: 1,
  INFO: 2,
};

/**
 * The severity each category ALWAYS carries. Severity is a property of the
 * detection rule itself (never of a threshold or a clock), so it can never
 * drift between requests.
 */
export const EXCEPTION_CATEGORY_SEVERITY: Record<ExceptionCategory, ExceptionSeverity> = {
  TRANSACTION_AGING: 'INFO',
  WORKFLOW_STUCK: 'ERROR',
  OPEN_PROCESSING_STEP: 'INFO',
  PROCESSING_DISCREPANCY: 'ATTENTION',
  PICKUP_NOT_RELEASED: 'INFO',
  DELIVERY_STEP_DEPTH: 'INFO',
  ITEM_ACCOUNTABILITY: 'ATTENTION',
  WORKFLOW_INCONSISTENCY: 'ERROR',
};

/**
 * The Manila business timezone. The Philippines observes no DST, so a fixed
 * +08:00 offset is exact year-round - business dates are derived from
 * SERVER-side timestamps, never from the browser clock.
 */
export const BUSINESS_TIME_ZONE = 'Asia/Manila' as const;
export const BUSINESS_TIME_ZONE_OFFSET_MS = 8 * 60 * 60 * 1000;

/** Manila calendar date (YYYY-MM-DD) of a timestamp. */
export function manilaBusinessDate(at: Date | string): string {
  const time = typeof at === 'string' ? Date.parse(at) : at.getTime();
  return new Date(time + BUSINESS_TIME_ZONE_OFFSET_MS).toISOString().slice(0, 10);
}
